import { Button, Dialog, List, makeStyles } from "@material-ui/core";
import { useEffect, useState } from "react";
import usePricingApi, { IdentifiablePricing } from "../../api/PricingApi";
import usePricingGroupApi, { PricingGroupElement } from "../../api/PricingGroupApi";
import PricingGroupMaterialSelectableListItem from "./PricingGroupMaterialSelectableListItem";
import { Material } from "../../api/MaterialApi";

interface Props {
  readonly customerId: number;
  readonly priceListId: number;
  readonly pricingGroupId?: number;
  readonly open: boolean;
  readonly onDialogClosed: () => void;
}

const useStyles = makeStyles(theme => ({
  button: {
    margin: theme.spacing(0.5)
  }
}));

const PricingGroupMaterialSelectionDialog = (props: Props) => {
  const classes = useStyles();
  const pricingGroupApi = usePricingGroupApi()
  const pricingApi = usePricingApi();
  const customerId = props.customerId;
  const priceListId = props.priceListId;
  const pricingGroupId = props.pricingGroupId;
  const dialogOpen = props.open;
  const handleDialogClosed = props.onDialogClosed;
  const [pricingGroupElements, setPricingGroupElements] = useState<PricingGroupElement[]>([]);
  const [selectedMaterialIds, setSelectedMaterialIds] = useState<number[]>([]);

  const fetchPricingGroupMaterials = () => {
    if (pricingGroupId !== undefined) {
      pricingGroupApi
        .fetchPricingGroupElements(pricingGroupId)
        .then(refreshMaterialsListView)
        .catch(error => console.warn(error));
    }
  };

  const refreshMaterialsListView = (pricingGroupElements: PricingGroupElement[]) => {
    pricingGroupElements.sort((element, anotherElement) => element.material.name.localeCompare(anotherElement.material.name));
    setPricingGroupElements(pricingGroupElements);
  };

  const handleAssignClicked = async () => {
    if (pricingGroupId !== undefined) {
        await pricingGroupApi.assignPricingGroupToCustomer(customerId, pricingGroupId);
        const fetchedPricings = await pricingApi.fetchPricings(customerId, priceListId);
        await updateSelectedPricings(fetchedPricings);
        await createNewPricings(fetchedPricings);
        props.onDialogClosed();
    };
  };

  const updateSelectedPricings = async (fetchedPricings: IdentifiablePricing[]) => {
    const pricingsWithUpdatedPrice = fetchedPricings
          .filter(pricing => selectedMaterialIds.includes(pricing.material.id))
          .map(pricing => ({ ...pricing, updatedUnitPrice: getUnitPriceForMaterialFromPricingGroup(pricing.material.id) }));
    console.debug(`pricingsWithUpdatedPrice: ${JSON.stringify(pricingsWithUpdatedPrice)}`);
    const pricingsWithMissingUpdatedPrice = pricingsWithUpdatedPrice.filter(pricing => pricing.updatedUnitPrice === undefined);
    if (pricingsWithMissingUpdatedPrice.length > 0) {
      console.warn(`Updated unit price could not be determined: ${JSON.stringify(pricingsWithMissingUpdatedPrice)}`);
    } else {
      for (const pricing of pricingsWithUpdatedPrice) {
        const updatedUnitPrice = pricing.updatedUnitPrice;
        if (updatedUnitPrice) {
          await pricingApi.updatePricing(customerId, priceListId, pricing.id, { ...pricing, unitPrice: updatedUnitPrice });
          console.debug(JSON.stringify(pricing));
        } else {
          console.warn(`Updated unit price could not be found: ${JSON.stringify(pricing)}`);
        }
      }
    }
  };

  const createNewPricings = async (fetchedPricings: IdentifiablePricing[]) => {
    const materialIdsOfPricings = fetchedPricings.map(pricing => pricing.material.id);
    const pricingsToBeCreated = selectedMaterialIds
      .filter(materialId => !materialIdsOfPricings.includes(materialId))
      .map(materialId => ({
        material: { id: materialId },
        unitPrice: getUnitPriceForMaterialFromPricingGroup(materialId)
      }));
    const pricingsWithMissingUnitPrice = pricingsToBeCreated.filter(pricing => pricing.unitPrice === undefined);
    if (pricingsWithMissingUnitPrice.length > 0) {
      console.warn(`Unit price could not be determined: ${JSON.stringify(pricingsWithMissingUnitPrice)}`);
    } else {
      for (const pricing of pricingsToBeCreated) {
        if (pricing.unitPrice) {
          await pricingApi.createPricing(customerId, priceListId, { ...pricing, unitPrice: pricing.unitPrice });
          console.debug(JSON.stringify(pricing));
        } else {
          console.warn(`Unit price could not be found: ${JSON.stringify(pricing)}`);
        }
      }
    }
  };

  const getUnitPriceForMaterialFromPricingGroup = (materialId: number) =>
    pricingGroupElements.find(pricingGroupElement => pricingGroupElement.material.id === materialId)?.unitCustomerPrice;

  const handleCancelClicked = () => {
    props.onDialogClosed();
  };

  const handleMaterialToggled = (material: Material) => (selected: boolean) => {
    const updatedSelectedMaterialIds = selectedMaterialIds.filter(materialId => materialId !== material.id);
    if (selected === true) {
      updatedSelectedMaterialIds.push(material.id);
    }
    console.debug(`updatedSelectedMaterialIds: ${updatedSelectedMaterialIds}`);
    setSelectedMaterialIds(updatedSelectedMaterialIds);
  };

  useEffect(() => {
    if (dialogOpen === true) {
      fetchPricingGroupMaterials();
      setSelectedMaterialIds([]);
    }
  }, [dialogOpen]);

  return (
    <Dialog open={dialogOpen} onClose={handleDialogClosed}>
      <List>
        { pricingGroupElements.map(pricingGroupElement =>
          <PricingGroupMaterialSelectableListItem
            pricingGroupElement={ pricingGroupElement }
            updated={ false }
            onMaterialToggled={ handleMaterialToggled(pricingGroupElement.material) }/>)
        }
      </List>
      <Button
        color='primary'
        className={classes.button}
        onClick={() => handleAssignClicked()}
        variant='contained'>
          Przypisz i zastosuj grupę cenową
      </Button>
      <Button
        color='secondary'
        className={classes.button}
        onClick={handleCancelClicked}
        variant='contained'>
          Zrezygnuj
      </Button>
    </Dialog>
  );
};

export default PricingGroupMaterialSelectionDialog;